import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { saveAs } from 'file-saver';
import heic2any from 'heic2any';
import { TablerIconsModule } from 'angular-tabler-icons';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MaterialModule } from 'src/app/material.module';
import { FormControl, FormGroup } from '@angular/forms';
import { SELECT_TYPE, VALIDATE_IMAGE } from 'src/app/shared/enums';
import { ToastService } from 'src/app/services/toast.service';
import { ImgDialogComponent } from './img-dialog/img-dialog.component';
import { SelectFileConfig } from 'src/app/shared/class/Select';
import { FILE_TYPES } from 'src/app/shared/constants';

@Component({
  selector: 'app-select-file',
  standalone: true,
  imports: [MaterialModule, MatTooltipModule, TablerIconsModule, CommonModule, TranslateModule],
  templateUrl: './select-file.component.html',
  styleUrl: './select-file.component.scss'
})
export class SelectFileComponent implements OnInit {
  @Input() fileSelectConfig: SelectFileConfig;
  @Input() controlName: string;
  @Input() formGroup: FormGroup;
  @ViewChild('fileInput') public fileInput: any;
  public selectedFile: any = null;
  public isDraggingOver = false;

  constructor(
    public dialog: MatDialog,
    private toastService: ToastService,
    private translateService: TranslateService,
    private http: HttpClient
  ) {}

  get disabled() {
    return !this.formGroup.get(this.controlName)?.enabled;
  }

  ngOnInit() {
    if (this.fileSelectConfig.fileUri) {
      this.fileSelectConfig.fileName = this.fileSelectConfig.fileUri.substring(this.fileSelectConfig.fileUri.lastIndexOf('/') + 1);
    }
  }

  async onFileSelect(event: any) {
    if (event.target.files.length > 0) {
      const file = event.target.files[0];

      if (this.fileSelectConfig.selectType == SELECT_TYPE.IMAGE) {
        // Check file size
        if (file.size > VALIDATE_IMAGE.MAX_FILE_SIZE) {
          this.toastService.error(this.translateService.instant('file is too large! please select a file smaller than 2mb'));
          return;
        }
        // Check image dimensions
        const img = new Image();
        img.src = URL.createObjectURL(file);
        const isValidImage = await new Promise<boolean>((resolve) => {
          img.onload = () => {
            const { width, height } = img;
            if (width < VALIDATE_IMAGE.MIN_DIMENSION || height < VALIDATE_IMAGE.MIN_DIMENSION) {
              this.toastService.error(this.translateService.instant('image is too small! minimum dimensions are 150x150 pixels'));
              resolve(false);
            }
            if (width > VALIDATE_IMAGE.MAX_DIMENSION || height > VALIDATE_IMAGE.MAX_DIMENSION) {
              this.toastService.error(this.translateService.instant('image is too large! maximum dimensions are 1000x1000 pixels'));
              resolve(false);
            }
            resolve(true); // Ensure resolution is valid before proceeding
          };
          img.onerror = (error) => {
            console.error('Error loading image', error);
            resolve(false); // Handle error loading image
          };
        });

        if (!isValidImage) {
          return; // Prevent further execution if the image is invalid
        }
      }

      if (this.fileSelectConfig.customValidate ? this.fileSelectConfig.customValidate(file) : true) {
        if (file.name.includes('.heic')) {
          await this.convertHEIC(file);
        } else {
          this.readFile(file);
        }
        this.updateFormValue(this.controlName, event.target.files);
      } else {
        this.toastService.error(this.translateService.instant('file not correct'));
      }
    }
  }

  updateFormValue(controlName: string, files: any = []) {
    if (!controlName) return;
    const controlFileName = `${controlName}File`;
    const file = files[0] || null;
    // Dynamically create control with suffix 'File' if not present
    if (file && !this.formGroup.contains(controlFileName)) {
      this.formGroup.addControl(controlFileName, new FormControl(null));
    }
    // Update the main control and file control
    this.formGroup.patchValue({
      [controlName]: file ? file.name : null,
      [controlFileName]: file
    });
  }

  isImageFile() {
    if (this.fileSelectConfig.selectType) {
      return this.fileSelectConfig.selectType == SELECT_TYPE.IMAGE;
    }
    if (this.fileSelectConfig.fileType && this.fileSelectConfig.fileType != '*') {
      const imgTypes = this.fileSelectConfig.fileType.split(' ').filter((type) => FILE_TYPES.img.includes(type));
      return imgTypes.length > 0;
    }
    return false;
  }

  clearFile(event: any, fileEvent: any) {
    event.stopPropagation();
    fileEvent.value = '';
    this.fileSelectConfig.fileUri = null;
    this.fileSelectConfig.fileName = '';
    this.fileSelectConfig.fileSize = '';
    this.updateFormValue(this.controlName);
  }

  openImage(event: any) {
    if (this.fileSelectConfig.fileUri && event.target != this.fileInput.nativeElement) {
      this.dialog.open(ImgDialogComponent, { data: this.fileSelectConfig.fileUri });
    } else {
      this.fileInput.nativeElement.click();
    }
  }

  onDownloadFile(event: any) {
    event.stopPropagation();
    if (this.fileSelectConfig.downloadFile && !this.fileSelectConfig.fileSize) {
      this.fileSelectConfig.downloadFile(event);
    } else if (this.fileSelectConfig.fileUri) {
      this.http.get(this.fileSelectConfig.fileUri, { responseType: 'blob' }).subscribe({
        next: (blob) => {
          saveAs(blob, this.fileSelectConfig.fileName);
        },
        error: ({ error }: HttpErrorResponse) => {
          this.toastService.error(`Download error: ${error}`);
        }
      });
    }
  }

  //drag drop file
  handleDragOver(evt: DragEvent) {
    this.isDraggingOver = true;
    evt.preventDefault();
    evt.stopPropagation();
  }

  handleDragLeave(evt: DragEvent) {
    evt.preventDefault();
    evt.stopPropagation();
    this.isDraggingOver = false;
  }

  async onDrop(evt: DragEvent) {
    evt.preventDefault();
    evt.stopPropagation();
    this.isDraggingOver = false;
    if (evt.dataTransfer) {
      for (let i = 0; i < evt.dataTransfer.files.length; i++) {
        const file = evt.dataTransfer.files[i];
        if (this.fileSelectConfig.customValidate ? this.fileSelectConfig.customValidate(file) : true) {
          if (file.name.includes('.heic')) {
            await this.convertHEIC(file);
          } else {
            this.readFile(file);
          }
          this.updateFormValue(this.controlName, evt.dataTransfer.files);
        } else {
          this.toastService.error(this.translateService.instant('file not correct'));
        }
      }
    }
  }

  async convertHEIC(file: any) {
    //if HEIC file
    const reader = new FileReader();
    reader.onload = async (e: ProgressEvent<FileReader>) => {
      if (e.target && e.target.result) {
        try {
          const blob = await heic2any({ blob: file });
          this.convertBlobToDataUrl(blob as Blob);
        } catch (error) {
          console.error('Error converting HEIC to JPEG/PNG:', error);
        }
      }
    };
    reader.readAsArrayBuffer(file);
  }

  private convertBlobToDataUrl(blob: Blob) {
    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      this.fileSelectConfig.fileUri = e.target ? e.target.result : null;
    };
    reader.readAsDataURL(blob);
  }

  private readFile(file: any) {
    this.fileSelectConfig.fileName = file?.name;
    this.fileSelectConfig.fileSize = this.formatFileSize(file?.size);
    const reader: FileReader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      this.fileSelectConfig.fileUri = e.target ? e.target.result : null;
    };
    reader.readAsDataURL(file);
  }

  formatFileSize(bytes: number) {
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let index = 0;
    while (bytes >= 1024 && index < units.length - 1) {
      bytes /= 1024;
      index++;
    }
    return `${bytes.toFixed(2)} ${units[index]}`;
  }
}
